// Manual print workbench. Pick a template, fill its variables, enqueue
// a job, watch its status. After the job is DONE — link to download
// the rendered PDF.
//
// Targets: shift leads / admins who need to reprint a passport or a
// label without going through the production execution flow. Permission
// gate: print.use.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import {
  PDF_SINK_AGENT_ID,
  printApi,
  type PrintAgentCapable,
  type PrintJobListItem,
  type PrintJobRead,
  type PrintTemplateRead,
} from "@/lib/api";

/** Label shown for the W8 "save to PDF" pseudo-agent (Print.tsx uses
 *  hardcoded RU strings, not i18n). */
const PDF_SINK_LABEL = "Не печатать — сохранить в PDF";
import { formatDateTime } from "@/lib/format";

type PrintTab = "templates" | "history";

export default function Print() {
  return (
    <RequirePermission codes="print.use">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const [tab, setTab] = useState<PrintTab>("templates");
  const [selected, setSelected] = useState<PrintTemplateRead | null>(null);

  return (
    <>
      <PageHeader
        eyebrow="печать"
        title="Печать"
        description="Этикетки, паспорта качества, упаковочные листы. Выберите шаблон или загляните в историю — все ранее напечатанное доступно для скачивания."
      />

      <nav className="flex items-center gap-1 border-b border-border mb-5">
        {[
          { k: "templates" as const, label: "Шаблоны" },
          { k: "history" as const, label: "История печати" },
        ].map(({ k, label }) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={
              "px-4 py-2 text-sm border-b-2 -mb-px transition-colors " +
              (tab === k
                ? "border-accent text-text"
                : "border-transparent text-text-muted hover:text-text")
            }
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === "templates" && <TemplatesPanel onPick={setSelected} />}
      {tab === "history" && <HistoryPanel />}

      {selected && <EnqueueModal template={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function TemplatesPanel({ onPick }: { onPick: (t: PrintTemplateRead) => void }) {
  const tpls = useQuery({
    queryKey: ["print", "templates"],
    queryFn: () => printApi.listTemplates({ include_archived: false }),
  });

  if (tpls.isLoading) return <LoadingState />;
  if (tpls.isError) return <ErrorState error={tpls.error} />;
  if (!tpls.data || tpls.data.items.length === 0) {
    return (
      <section className="card p-8 text-center text-text-muted">
        <p>Шаблонов печати ещё нет.</p>
        <p className="text-xs mt-2">
          Загрузить шаблон может администратор в разделе печати.
        </p>
      </section>
    );
  }

  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {tpls.data.items.map((t) => (
        <TemplateCard key={t.id} t={t} onPick={() => onPick(t)} />
      ))}
    </section>
  );
}

function TemplateCard({
  t,
  onPick,
}: {
  t: PrintTemplateRead;
  onPick: () => void;
}) {
  return (
    <article className="card p-5 flex flex-col gap-3">
      <header>
        <p className="font-mono text-xs uppercase tracking-widest text-text-muted">
          {t.brand} · {t.kind}
        </p>
        <h3 className="text-base mt-1">{t.name}</h3>
        {t.description && <p className="text-text-muted text-sm mt-1">{t.description}</p>}
      </header>
      <p className="text-text-muted text-xs font-mono">
        {t.page_width_mm}×{t.page_height_mm} мм · v{t.version}
      </p>
      <button className="btn btn-primary mt-auto" onClick={onPick}>
        🖨 Печать
      </button>
    </article>
  );
}

/** Variable types from packages/print-plugin/packages/shared (plugin format). */
type VariableType = "text" | "number" | "date" | "enum" | "auto_increment";

interface PluginVariable {
  key: string;
  label: string;
  type: VariableType;
  required: boolean;
  /** text */
  maxLength?: number;
  /** text/number/date/enum */
  default?: string | number;
  /** number */
  min?: number;
  max?: number;
  /** enum */
  enumOptions?: string[];
  /** auto_increment — operator types a seed; renderer parses prefix/startAt/padWidth from it. */
  prefix?: string;
  padWidth?: number;
  startAt?: number;
  editableByOperator?: boolean;
}

interface DerivedSchema {
  /** Plugin-shape variables, fully typed. Empty array = template with
   *  no operator inputs (renderer uses defaults). */
  variables: PluginVariable[];
  /** True if we recognised the plugin format; false if we fell back to
   *  legacy heuristics or there was nothing to parse. */
  isPluginFormat: boolean;
}

/** Parse `variables_schema` from the backend. Recognises:
 *   1. Plugin format `{ variables: [{ key, type, label, required, ... }] }`.
 *   2. JSON-Schema-ish `{ properties: { name: { type, description } }, required }`.
 *   3. Flat `{ key: type }` (where type is the literal string "text" / "number" / ...).
 *
 *  Everything is mapped to PluginVariable so the form renderer has one
 *  shape to deal with. Unparseable input → null. */
function deriveSchema(schema: unknown): DerivedSchema | null {
  if (!schema || typeof schema !== "object") return null;

  // (1) Plugin format.
  if (
    "variables" in schema &&
    Array.isArray((schema as { variables?: unknown }).variables)
  ) {
    const raw = (schema as { variables: unknown[] }).variables;
    const variables: PluginVariable[] = raw
      .filter((v): v is Record<string, unknown> => !!v && typeof v === "object")
      .map((v) => ({
        key: String(v.key ?? ""),
        label: String(v.label ?? v.key ?? ""),
        type: (v.type as VariableType) ?? "text",
        required: v.required === true,
        maxLength: typeof v.maxLength === "number" ? v.maxLength : undefined,
        default:
          typeof v.default === "string" || typeof v.default === "number" ? v.default : undefined,
        min: typeof v.min === "number" ? v.min : undefined,
        max: typeof v.max === "number" ? v.max : undefined,
        enumOptions: Array.isArray(v.enumOptions) ? (v.enumOptions as string[]) : undefined,
        prefix: typeof v.prefix === "string" ? v.prefix : undefined,
        padWidth: typeof v.padWidth === "number" ? v.padWidth : undefined,
        startAt: typeof v.startAt === "number" ? v.startAt : undefined,
        editableByOperator: v.editableByOperator === true,
      }))
      .filter((v) => v.key);
    return { variables, isPluginFormat: true };
  }

  // (2) JSON-Schema.
  if (
    "properties" in schema &&
    typeof (schema as { properties?: unknown }).properties === "object" &&
    (schema as { properties?: unknown }).properties !== null
  ) {
    const rawRequired = (schema as { required?: unknown }).required;
    const required = new Set<string>(
      Array.isArray(rawRequired) ? (rawRequired as string[]) : [],
    );
    const props = (schema as { properties: Record<string, unknown> }).properties;
    const variables: PluginVariable[] = Object.entries(props).map(([key, raw]) => {
      const def = (raw ?? {}) as { type?: string };
      return {
        key,
        label: key,
        type:
          def.type === "number" || def.type === "integer"
            ? "number"
            : def.type === "boolean"
              ? "text"
              : "text",
        required: required.has(key),
      };
    });
    return { variables, isPluginFormat: false };
  }

  // (3) Flat key→type map.
  const entries = Object.entries(schema as Record<string, unknown>);
  if (entries.length === 0) return { variables: [], isPluginFormat: false };
  return {
    variables: entries.map(([key, raw]) => ({
      key,
      label: key,
      type: typeof raw === "number" ? "number" : "text",
      required: true,
    })),
    isPluginFormat: false,
  };
}

/** Compose a seed-string back for auto_increment when the operator
 *  didn't bother editing it: "prefix" + zero-padded startAt. */
function defaultSeedFor(v: PluginVariable): string {
  const prefix = v.prefix ?? "";
  const start = v.startAt ?? 1;
  const pad = v.padWidth ?? 4;
  return `${prefix}${String(start).padStart(pad, "0")}`;
}

function EnqueueModal({ template, onClose }: { template: PrintTemplateRead; onClose: () => void }) {
  const derived = deriveSchema(template.variables_schema);
  const variables = derived?.variables ?? [];
  const hasFields = variables.length > 0;
  const hasAutoInc = variables.some((v) => v.type === "auto_increment");

  // Pre-fill defaults so the operator only has to touch what matters.
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const v of variables) {
      if (v.type === "auto_increment") {
        init[v.key] = v.editableByOperator ? defaultSeedFor(v) : "";
      } else if (v.default !== undefined) {
        init[v.key] = String(v.default);
      } else {
        init[v.key] = "";
      }
    }
    return init;
  });
  // Print run length: useful when at least one variable is
  // auto_increment (= N labels with increasing serial numbers).
  const [total, setTotal] = useState<string>("1");
  // Fallback JSON when no schema at all.
  const [rawParams, setRawParams] = useState<string>("{}");
  const [err, setErr] = useState<string | null>(null);
  const [job, setJob] = useState<PrintJobRead | null>(null);

  // Agent + printer picker.  Pre-selected from the template's admin-set
  // defaults; operator can override.  Empty `agentId` = auto (any
  // capable agent races for the job).  `printerName` is per-agent —
  // resets when agent changes unless it's still valid for the new one.
  // Ручная печать: показываем ВСЕХ онлайн-агентов без фильтра по kind
  // шаблона — kind у нас identifier, а не routing-критерий.  Оператор
  // сам решает на каком принтере печатать.
  const agentsQ = useQuery({
    queryKey: ["print", "agents", "online"],
    queryFn: () => printApi.listOnlineAgents(),
    refetchInterval: 15_000,
  });
  const [agentId, setAgentId] = useState<string>(template.default_agent_id ?? "");
  const [printerName, setPrinterName] = useState<string>(
    template.default_printer_name ?? "",
  );
  // When the operator switches agents, drop printerName unless the
  // chosen one is still in the new agent's printer list.
  const selectedAgent = agentsQ.data?.items.find((a) => a.id === agentId) ?? null;
  useEffect(() => {
    if (!selectedAgent) {
      setPrinterName("");
      return;
    }
    const hasPicked = selectedAgent.printers.some(
      (p) => (p.windows_name ?? p.name) === printerName,
    );
    if (!hasPicked) setPrinterName("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const mut = useMutation({
    mutationFn: () => {
      let params: Record<string, unknown>;
      if (hasFields) {
        params = {};
        for (const v of variables) {
          const raw = values[v.key] ?? "";
          if (raw === "" && v.required) {
            throw new Error(`Поле «${v.label}» обязательно для заполнения.`);
          }
          if (raw === "" && !v.required) continue;

          if (v.type === "number") {
            const n = Number(raw);
            if (!Number.isFinite(n)) {
              throw new Error(`Поле «${v.label}» должно быть числом.`);
            }
            if (v.min !== undefined && n < v.min) {
              throw new Error(`«${v.label}» меньше минимума ${v.min}.`);
            }
            if (v.max !== undefined && n > v.max) {
              throw new Error(`«${v.label}» больше максимума ${v.max}.`);
            }
            params[v.key] = n;
          } else if (v.type === "enum") {
            params[v.key] = raw;
          } else {
            // text / date / auto_increment — все строкой.
            params[v.key] = raw;
          }
        }
      } else if (!derived) {
        try {
          params = JSON.parse(rawParams) as Record<string, unknown>;
        } catch {
          throw new Error("params: невалидный JSON.");
        }
      } else {
        params = {};
      }

      const totalN = Number(total);
      if (!Number.isFinite(totalN) || totalN < 1) {
        throw new Error("Тираж должен быть целым числом от 1.");
      }

      return printApi.enqueueJob({
        kind: template.kind,
        template_id: template.id,
        params,
        total: totalN,
        ...(agentId ? { agent_id: agentId } : {}),
        ...(printerName ? { printer_name: printerName } : {}),
      });
    },
    onSuccess: (j) => setJob(j),
    onError: (e) => setErr(e instanceof Error ? e.message : "Ошибка"),
  });

  // Poll the job status until it leaves QUEUED/RUNNING.  Normalize
  // case here too — backend ships lowercase.
  const jobQ = useQuery({
    queryKey: ["print", "job", job?.job_id],
    queryFn: () => printApi.getJob(job!.job_id),
    enabled: !!job &&
      (() => {
        const s = String(job.status).toUpperCase();
        return s === "QUEUED" || s === "RUNNING";
      })(),
    refetchInterval: 1500,
  });

  useEffect(() => {
    if (jobQ.data) setJob(jobQ.data);
  }, [jobQ.data]);

  // Backend returns lowercase status (queued/running/done/failed/...),
  // local typings historically use UPPERCASE — normalize before comparing
  // so the "DONE → show download" branch actually fires.
  const status = (job?.status ?? "—").toString().toUpperCase();
  const isDone = status === "DONE";
  const isFailed = status === "FAILED" || status === "CANCELLED";

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <p className="font-mono text-xs uppercase tracking-widest text-text-muted">
            {template.brand} · {template.kind}
          </p>
          <h2 className="text-lg mt-1">{template.name}</h2>
        </header>

        {!job && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setErr(null);
              mut.mutate();
            }}
            className="space-y-3"
          >
            {!derived && (
              <p className="text-text-muted text-sm">
                У шаблона нет описания переменных. Введите параметры JSON-ом ниже.
              </p>
            )}
            {derived && variables.length === 0 && (
              <p className="text-text-muted text-sm">
                У этого шаблона нет переменных — задайте тираж и нажмите «Поставить в очередь».
              </p>
            )}

            {variables.map((v) => (
              <VariableField
                key={v.key}
                v={v}
                value={values[v.key] ?? ""}
                onChange={(val) => setValues((s) => ({ ...s, [v.key]: val }))}
              />
            ))}

            {!derived && (
              <Field label="params (JSON)">
                <textarea
                  value={rawParams}
                  onChange={(e) => setRawParams(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
                />
              </Field>
            )}

            <AgentPickerField
              agents={agentsQ.data?.items ?? []}
              loading={agentsQ.isLoading}
              value={agentId}
              onChange={setAgentId}
              printerName={printerName}
              onPrinterChange={setPrinterName}
            />

            <Field
              label="Тираж"
              hint={
                hasAutoInc
                  ? "Количество этикеток в партии. Серийные номера будут автоматически увеличиваться от начального."
                  : "Сколько копий напечатать."
              }
            >
              <input
                type="number"
                min={1}
                step={1}
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-sm tabular-nums"
              />
            </Field>

            {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" className="btn" onClick={onClose}>
                Отмена
              </button>
              <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
                {mut.isPending ? "Постановка…" : "🖨 Поставить в очередь"}
              </button>
            </div>
          </form>
        )}

        {job && (
          <section className="space-y-3">
            <p className="text-text-muted text-sm">
              Задание создано. ID: <span className="font-mono">{job.job_id}</span>
            </p>
            <div className="flex items-center gap-3">
              <span
                className={[
                  "pill font-mono text-xs",
                  isDone ? "pill-ok" : isFailed ? "pill-danger" : "pill-warn",
                ].join(" ")}
              >
                {status}
              </span>
              {job.rendered !== undefined && job.total !== undefined && (
                <span className="text-text-muted text-xs">
                  {job.rendered}/{job.total}
                </span>
              )}
              {job.finished_at && (
                <span className="text-text-muted text-xs">{formatDateTime(job.finished_at)}</span>
              )}
            </div>

            {job.error && (
              <ErrorBanner>
                <span className="font-mono text-xs">{job.error}</span>
              </ErrorBanner>
            )}

            {isDone && (
              <PdfActions jobId={job.job_id} autoDownload={agentId === PDF_SINK_AGENT_ID} />
            )}
            {isDone && agentId === PDF_SINK_AGENT_ID && (
              <p className="text-text-muted text-xs">
                Задание сохранено в историю как «{PDF_SINK_LABEL}». PDF скачивается автоматически;
                напечатать можно позже из истории.
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" className="btn" onClick={onClose}>
                Закрыть
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * Print-agent picker.  Empty value = "auto" (any capable online agent
 * claims the job, as before this feature).  When the operator picks a
 * specific agent the job is pre-assigned to them and dispatched
 * directly — no claim race, the job won't accidentally land on
 * another printer.
 *
 * The list refreshes every 15s in the background so an agent that
 * comes online while the modal is open becomes selectable; an agent
 * that disappears stops being listed.
 */
function AgentPickerField({
  agents,
  loading,
  value,
  onChange,
  printerName,
  onPrinterChange,
}: {
  agents: PrintAgentCapable[];
  loading: boolean;
  value: string;
  onChange: (v: string) => void;
  printerName: string;
  onPrinterChange: (v: string) => void;
}) {
  const hint = loading
    ? "Загрузка списка агентов…"
    : agents.length === 0
      ? "Сейчас нет онлайн-агентов. Задание будет создано в очереди — заберёт первый подключившийся."
      : "«Авто» — задание возьмёт любой онлайн-агент (диспетчер фильтрует по kind шаблона при автодиспетче). Выберите конкретного, если печать нужна именно с него.";

  const selected = agents.find((a) => a.id === value) ?? null;
  const printers = selected?.printers ?? [];

  return (
    <>
      <Field label="Агент печати" hint={hint}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
          disabled={loading}
        >
          <option value="">— Авто (любой подходящий) —</option>
          <option value={PDF_SINK_AGENT_ID}>📄 {PDF_SINK_LABEL}</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      {selected && (
        <Field
          label="Принтер"
          hint={
            printers.length === 0
              ? "У этого агента нет принтеров, способных печатать данный тип."
              : "«Авто» — агент сам выберет первый подходящий принтер. Выберите конкретный, если нужна именно та модель."
          }
        >
          <select
            value={printerName}
            onChange={(e) => onPrinterChange(e.target.value)}
            className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
          >
            <option value="">— Авто (любой подходящий) —</option>
            {printers.map((p) => (
              <option key={p.id} value={p.windows_name || p.name}>
                {p.windows_name ? `${p.name} · ${p.windows_name}` : p.name}
              </option>
            ))}
          </select>
        </Field>
      )}
    </>
  );
}

/**
 * Two actions for a rendered PDF: open in a new tab (preview) and
 * download to disk.  Both go through `printApi.fetchJobPdf` so the
 * bearer token is attached — a plain `<a href>` would 401 because
 * browsers don't propagate Authorization headers to anchor clicks.
 *
 * The blob URL is created on demand and reused for "Open" within the
 * same modal session; revoked when the component unmounts to keep
 * memory clean.
 */
function PdfActions({ jobId, autoDownload = false }: { jobId: string; autoDownload?: boolean }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [pending, setPending] = useState<"open" | "download" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const autoRan = useRef(false);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // W8 "save to PDF": once the render is done, push the file to the browser
  // automatically (the operator picked "save", not "print"). Guard so it
  // fires exactly once.
  useEffect(() => {
    if (autoDownload && !autoRan.current) {
      autoRan.current = true;
      void onDownload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload]);

  const obtainUrl = async (): Promise<string> => {
    if (blobUrl) return blobUrl;
    const blob = await printApi.fetchJobPdf(jobId);
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return url;
  };

  const onOpen = async () => {
    setErr(null);
    setPending("open");
    try {
      const url = await obtainUrl();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось открыть PDF");
    } finally {
      setPending(null);
    }
  };

  const onDownload = async () => {
    setErr(null);
    setPending("download");
    try {
      const url = await obtainUrl();
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-${jobId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось скачать PDF");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        className="btn"
        onClick={onOpen}
        disabled={pending !== null}
      >
        {pending === "open" ? "Открытие…" : "👁 Открыть PDF"}
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onDownload}
        disabled={pending !== null}
      >
        {pending === "download" ? "Скачивание…" : "⬇ Скачать PDF"}
      </button>
      {err && <span className="text-state-danger text-xs">{err}</span>}
    </div>
  );
}

function VariableField({
  v,
  value,
  onChange,
}: {
  v: PluginVariable;
  value: string;
  onChange: (val: string) => void;
}) {
  const label = v.label + (v.required ? " *" : "");

  if (v.type === "enum") {
    return (
      <Field label={label}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
        >
          {!v.required && <option value="">— не задано —</option>}
          {(v.enumOptions ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </Field>
    );
  }

  if (v.type === "date") {
    return (
      <Field label={label}>
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm tabular-nums"
        />
      </Field>
    );
  }

  if (v.type === "number") {
    const minMaxHint = [
      v.min !== undefined ? `мин ${v.min}` : null,
      v.max !== undefined ? `макс ${v.max}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <Field label={label} hint={minMaxHint || undefined}>
        <input
          type="number"
          step="any"
          min={v.min}
          max={v.max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono tabular-nums"
        />
      </Field>
    );
  }

  if (v.type === "auto_increment") {
    const seed = defaultSeedFor(v);
    const hint = v.editableByOperator
      ? `Начальный серийник партии. По умолчанию: «${seed}» (префикс «${
          v.prefix ?? ""
        }» + ${v.padWidth ?? 4}-значный номер от ${v.startAt ?? 1}).`
      : `Серийный номер генерируется автоматически: «${v.prefix ?? ""}» + ${
          v.padWidth ?? 4
        }-значный номер от ${v.startAt ?? 1}.`;
    if (!v.editableByOperator) {
      return (
        <Field label={label} hint={hint}>
          <input
            type="text"
            value={seed}
            disabled
            className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono text-text-muted"
          />
        </Field>
      );
    }
    return (
      <Field label={label} hint={hint}>
        <input
          type="text"
          value={value}
          placeholder={seed}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm font-mono"
        />
      </Field>
    );
  }

  // type === "text"
  const hint = v.maxLength ? `до ${v.maxLength} символов` : undefined;
  return (
    <Field label={label} hint={hint}>
      <input
        type="text"
        value={value}
        maxLength={v.maxLength}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-md bg-surface border border-border text-sm"
      />
    </Field>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-text-muted text-xs mt-1">{hint}</p>}
    </label>
  );
}

// ===================================================================
// HistoryPanel — список последних print-jobs со скачиванием PDF.
//
// Auto-refresh каждые 5с пока вкладка открыта.  Кнопки «Открыть» /
// «Скачать» доступны для row с has_pdf=true (render завершён);
// статус «не закончен» / «failed» — кнопки не показываем.
// ===================================================================

// Exported so an admin-only page can reuse the same list without
// the surrounding operator-facing tabs / form on /print.
export function HistoryPanel() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const jobsQ = useQuery({
    queryKey: ["print", "jobs", "history", statusFilter],
    queryFn: () =>
      printApi.listJobs({
        limit: 100,
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
    refetchInterval: 5000,
  });

  const refresh = () =>
    void qc.invalidateQueries({ queryKey: ["print", "jobs", "history"] });

  // Мультиселект для пакетного удаления.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState<string | null>(null);

  const items = jobsQ.data?.items ?? [];
  const allSelected = items.length > 0 && items.every((j) => selected.has(j.job_id));

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(() => (allSelected ? new Set() : new Set(items.map((j) => j.job_id))));
  const clearSelection = () => setSelected(new Set());

  const onBulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Удалить выбранные задания (${ids.length})? Действие отменяет рендер и удаляет PDF. Необратимо.`,
      )
    ) {
      return;
    }
    setBulkErr(null);
    setBulkBusy(true);
    try {
      const res = await printApi.deleteJobsBulk(ids);
      clearSelection();
      refresh();
      if (res.failed.length > 0) {
        setBulkErr(
          `Не удалось удалить ${res.failed.length} из ${ids.length}: ` +
            res.failed.map((f) => f.job_id).join(", "),
        );
      }
    } catch (e) {
      setBulkErr(e instanceof Error ? e.message : "Не удалось удалить выбранные");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <section className="space-y-3">
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
          Фильтр статуса
        </span>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            clearSelection();
          }}
          className="px-3 py-1.5 rounded-md bg-surface border border-border text-sm"
        >
          <option value="">— все —</option>
          <option value="queued">queued (ждёт)</option>
          <option value="running">running (рендерится)</option>
          <option value="done">done (PDF готов)</option>
          <option value="failed">failed</option>
          <option value="cancelled">cancelled</option>
        </select>
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={onBulkDelete}
              disabled={bulkBusy}
            >
              {bulkBusy ? "Удаляю…" : `🗑 Удалить выбранные (${selected.size})`}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={clearSelection}
              disabled={bulkBusy}
            >
              Снять выбор
            </button>
          </div>
        )}
        <span className="text-text-muted text-xs ml-auto">
          обновляется автоматически каждые 5с
        </span>
      </div>

      {bulkErr && (
        <div className="card p-3 text-state-danger text-sm">{bulkErr}</div>
      )}

      {jobsQ.isLoading && <LoadingState />}
      {jobsQ.isError && <ErrorState error={jobsQ.error} />}
      {jobsQ.data && jobsQ.data.items.length === 0 && (
        <div className="card p-8 text-center text-text-muted text-sm">
          {statusFilter
            ? `Заданий со статусом «${statusFilter}» нет.`
            : "Заданий печати пока нет."}
        </div>
      )}

      {jobsQ.data && jobsQ.data.items.length > 0 && (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-muted text-xs font-mono uppercase border-b border-border">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    title="Выбрать все на странице"
                    aria-label="Выбрать все"
                  />
                </th>
                <th className="text-left px-3 py-2">шаблон</th>
                <th className="text-left px-3 py-2">тип</th>
                <th className="text-left px-3 py-2">статус</th>
                <th className="text-right px-3 py-2">тираж</th>
                <th className="text-left px-3 py-2">агент · принтер</th>
                <th className="text-left px-3 py-2">создан</th>
                <th className="text-left px-3 py-2">напечатан</th>
                <th className="text-right px-3 py-2 pr-3">PDF</th>
              </tr>
            </thead>
            <tbody>
              {jobsQ.data.items.map((j) => (
                <HistoryRow
                  key={j.job_id}
                  j={j}
                  onChanged={refresh}
                  selected={selected.has(j.job_id)}
                  onToggleSelect={() => toggleOne(j.job_id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function HistoryRow({
  j,
  onChanged,
  selected,
  onToggleSelect,
}: {
  j: PrintJobListItem;
  onChanged: () => void;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const [reprintOpen, setReprintOpen] = useState(false);
  const [busy, setBusy] = useState<"delete" | "reprint" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const status = j.status.toUpperCase();
  const isDone = status === "DONE";
  const isFailed = status === "FAILED" || status === "CANCELLED";
  const isRunning = status === "QUEUED" || status === "RUNNING";
  const pillCls = isDone
    ? "pill-ok"
    : isFailed
      ? "pill-danger"
      : isRunning
        ? "pill-warn"
        : "";
  const agentLine =
    j.agent_id === PDF_SINK_AGENT_ID
      ? `📄 ${PDF_SINK_LABEL}`
      : j.agent_name
        ? j.printer_name
          ? `${j.agent_name} · ${j.printer_name}`
          : j.agent_name
        : "—";

  const onDelete = async () => {
    if (!window.confirm(`Удалить задание ${j.job_id}? Действие отменяет рендер и удаляет PDF.`)) {
      return;
    }
    setErr(null);
    setBusy("delete");
    try {
      await printApi.deleteJob(j.job_id);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось удалить");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <tr className="border-b border-border/40 hover:bg-surface/40">
        <td className="px-3 py-2 w-8">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            aria-label={`Выбрать задание ${j.job_id}`}
          />
        </td>
        <td className="px-3 py-2">
          <div className="text-text">{j.template_name}</div>
          <div className="font-mono text-xs text-text-muted">{j.job_id}</div>
        </td>
        <td className="px-3 py-2 font-mono text-xs text-text-muted">{j.kind || "—"}</td>
        <td className="px-3 py-2">
          <span className={`pill font-mono text-xs ${pillCls}`}>{status}</span>
          {j.error && (
            <div className="text-state-danger text-xs mt-1 font-mono max-w-[20rem] truncate" title={j.error}>
              {j.error}
            </div>
          )}
          {err && <div className="text-state-danger text-xs mt-1">{err}</div>}
        </td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">
          {j.rendered}/{j.total}
        </td>
        <td className="px-3 py-2 text-text-muted">
          {agentLine}
          {j.printed_at && (
            <div className="text-xs text-text-muted">
              напечатано {formatDateTime(j.printed_at)}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-text-muted text-xs">
          {formatDateTime(j.created_at)}
        </td>
        <td className="px-3 py-2 text-text-muted text-xs">
          {j.finished_at ? formatDateTime(j.finished_at) : "—"}
        </td>
        <td className="px-3 py-2 pr-3">
          <div className="inline-flex items-center gap-1 justify-end">
            {j.has_pdf && <HistoryPdfButtons jobId={j.job_id} />}
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setReprintOpen((v) => !v)}
              disabled={busy !== null}
              title="Создать новое задание (тот же шаблон/параметры) и выбрать куда печатать"
            >
              🖨
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={onDelete}
              disabled={busy !== null}
              title="Удалить задание и PDF"
            >
              {busy === "delete" ? "…" : "🗑"}
            </button>
          </div>
        </td>
      </tr>
      {reprintOpen && (
        <tr className="border-b border-border/40 bg-surface/30">
          <td colSpan={9} className="px-3 py-3">
            <ReprintInline
              busy={busy === "reprint"}
              onCancel={() => setReprintOpen(false)}
              onConfirm={async (target) => {
                setErr(null);
                setBusy("reprint");
                try {
                  await printApi.reprintJob(j.job_id, target);
                  setReprintOpen(false);
                  onChanged();
                } catch (e) {
                  setErr(e instanceof Error ? e.message : "Не удалось перепечатать");
                } finally {
                  setBusy(null);
                }
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ReprintInline({
  busy,
  onCancel,
  onConfirm,
}: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: (target: { agent_id?: string; printer_name?: string }) => Promise<void>;
}) {
  // Reprint: тоже все онлайн-агенты, не фильтруем по kind исходного job'а.
  const agentsQ = useQuery({
    queryKey: ["print", "agents", "online"],
    queryFn: () => printApi.listOnlineAgents(),
  });
  const [agentId, setAgentId] = useState<string>("");
  const [printerName, setPrinterName] = useState<string>("");

  const selected = agentsQ.data?.items.find((a) => a.id === agentId) ?? null;
  const printers = selected?.printers ?? [];

  useEffect(() => {
    setPrinterName("");
  }, [agentId]);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
          Агент
        </span>
        <select
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          className="px-3 py-2 rounded-md bg-surface border border-border text-sm"
          disabled={agentsQ.isLoading}
        >
          <option value="">— Авто (любой подходящий) —</option>
          <option value={PDF_SINK_AGENT_ID}>📄 {PDF_SINK_LABEL}</option>
          {(agentsQ.data?.items ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>
      {selected && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Принтер
          </span>
          <select
            value={printerName}
            onChange={(e) => setPrinterName(e.target.value)}
            className="px-3 py-2 rounded-md bg-surface border border-border text-sm"
            disabled={printers.length === 0}
          >
            <option value="">— Авто (любой подходящий) —</option>
            {printers.map((p) => (
              <option key={p.id} value={p.windows_name || p.name}>
                {p.windows_name ? `${p.name} · ${p.windows_name}` : p.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() =>
            void onConfirm({
              ...(agentId ? { agent_id: agentId } : {}),
              ...(printerName ? { printer_name: printerName } : {}),
            })
          }
          disabled={busy}
        >
          {busy ? "Запуск…" : "🖨 Напечатать"}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Отмена
        </button>
      </div>
    </div>
  );
}

function HistoryPdfButtons({ jobId }: { jobId: string }) {
  const [pending, setPending] = useState<"open" | "download" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const fetchUrl = async (): Promise<string> => {
    const blob = await printApi.fetchJobPdf(jobId);
    return URL.createObjectURL(blob);
  };

  const onOpen = async () => {
    setErr(null);
    setPending("open");
    try {
      const url = await fetchUrl();
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke later — the new tab needs the URL alive a bit; 60s is plenty.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не открылся");
    } finally {
      setPending(null);
    }
  };

  const onDownload = async () => {
    setErr(null);
    setPending("download");
    try {
      const url = await fetchUrl();
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-${jobId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не скачался");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="inline-flex items-center gap-1 justify-end">
      <button
        type="button"
        className="btn btn-sm"
        onClick={onOpen}
        disabled={pending !== null}
        title="Открыть PDF в новой вкладке"
      >
        {pending === "open" ? "…" : "👁"}
      </button>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={onDownload}
        disabled={pending !== null}
        title="Скачать PDF"
      >
        {pending === "download" ? "…" : "⬇"}
      </button>
      {err && <span className="text-state-danger text-xs">{err}</span>}
    </div>
  );
}
